// Wertelisten und deutsche Bezeichnungen der Stammdaten – EINE Quelle für Verträge (z.enum), Audit-Texte auf dem
// Server und Auswahlfelder der Oberfläche. Die Werte entsprechen den CHECK-Constraints in db/migrations/0002.

export const ITEM_KINDS = ['goods', 'service', 'rental', 'empties', 'set'] as const
export type ItemKind = (typeof ITEM_KINDS)[number]
export const ITEM_KIND_LABELS: Record<ItemKind, string> = {
  goods: 'Ware',
  service: 'Dienstleistung',
  rental: 'Leihartikel',
  empties: 'Leergut',
  set: 'Set',
}

export const DATE_TRACKINGS = ['none', 'best_before', 'use_by'] as const
export type DateTracking = (typeof DATE_TRACKINGS)[number]
export const DATE_TRACKING_LABELS: Record<DateTracking, string> = {
  none: 'ohne Datum',
  best_before: 'Mindesthaltbarkeitsdatum',
  use_by: 'Verbrauchsdatum',
}

export const PACKAGINGS = ['none', 'single_use', 'single_use_deposit', 'reusable'] as const
export type Packaging = (typeof PACKAGINGS)[number]
export const PACKAGING_LABELS: Record<Packaging, string> = {
  none: 'ohne Angabe',
  single_use: 'Einweg ohne Pfand',
  single_use_deposit: 'Einweg mit Pfand',
  reusable: 'Mehrweg',
}

export const OWNERSHIPS = ['own', 'supplier', 'customer'] as const
export type Ownership = (typeof OWNERSHIPS)[number]
export const OWNERSHIP_LABELS: Record<Ownership, string> = {
  own: 'eigene Ware',
  supplier: 'Ware des Lieferanten',
  customer: 'Ware des Kunden',
}

export const MIN_AGES = [0, 16, 18] as const
export type MinAge = (typeof MIN_AGES)[number]
export const minAgeLabel = (age: number): string => (age > 0 ? `ab ${age} Jahren` : 'keine Altersgrenze')

export const PARTNER_TYPES = ['private', 'business', 'public'] as const
export type PartnerType = (typeof PARTNER_TYPES)[number]
export const PARTNER_TYPE_LABELS: Record<PartnerType, string> = {
  private: 'Privatperson',
  business: 'Unternehmen',
  public: 'Öffentliche Stelle',
}

export const PARTNER_ROLES = ['customer', 'supplier'] as const
export type PartnerRole = (typeof PARTNER_ROLES)[number]
export const PARTNER_ROLE_LABELS: Record<PartnerRole, { one: string; many: string }> = {
  customer: { one: 'Kunde', many: 'Kunden' },
  supplier: { one: 'Lieferant', many: 'Lieferanten' },
}

export const ADDRESS_KINDS = ['billing', 'shipping'] as const
export type AddressKind = (typeof ADDRESS_KINDS)[number]
export const ADDRESS_KIND_LABELS: Record<AddressKind, string> = { billing: 'Rechnungsanschrift', shipping: 'Lieferanschrift' }

export const COLLECTIVE_INVOICINGS = ['none', 'weekly', 'biweekly', 'monthly'] as const
export type CollectiveInvoicing = (typeof COLLECTIVE_INVOICINGS)[number]
export const COLLECTIVE_INVOICING_LABELS: Record<CollectiveInvoicing, string> = {
  none: 'Einzelrechnung',
  weekly: 'Sammelrechnung wöchentlich',
  biweekly: 'Sammelrechnung 14-tägig',
  monthly: 'Sammelrechnung monatlich',
}

export const EINVOICE_FORMATS = ['xrechnung', 'zugferd', 'pdf'] as const
export type EinvoiceFormat = (typeof EINVOICE_FORMATS)[number]
export const EINVOICE_FORMAT_LABELS: Record<EinvoiceFormat, string> = {
  xrechnung: 'XRechnung',
  zugferd: 'ZUGFeRD',
  pdf: 'PDF',
}
/** Anzeige für einvoiceFormat = null (§5.1): XML nur, wo die Pflicht greift */
export const EINVOICE_FORMAT_DEFAULT_LABEL = 'Nach gesetzlicher Regel'

// E-Rechnung: Auswahllisten aus EINER Quelle (shared/einvoice.ts) – auch die Vorprüfung des Moduls einvoice nutzt sie
export {
  EINVOICE_SYNTAXES,
  EINVOICE_SYNTAX_LABELS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  VATEX_CODES,
  vatexLabel,
  type EInvoiceSyntax,
  type PartnerPaymentMethod,
  type VatexCode,
} from '@shared/einvoice'
/** Anzeige für einvoiceSyntax = null */
export const EINVOICE_SYNTAX_DEFAULT_LABEL = 'Vorgabe des Betriebs'

// Steuerfall je Kunde (AP2c): Auswahlliste aus EINER Quelle (shared/taxCase.ts) – auch der Belegkern nutzt sie
export {
  TAX_CASES,
  TAX_CASE_LABELS,
  TAX_CASE_CATEGORY,
  TAX_CASE_SUBSTITUTE_CATEGORIES,
  type TaxCase,
} from '@shared/taxCase'

export const ORDER_CHANNELS = ['email', 'portal', 'edi', 'phone'] as const
export type OrderChannel = (typeof ORDER_CHANNELS)[number]
export const ORDER_CHANNEL_LABELS: Record<OrderChannel, string> = {
  email: 'E-Mail',
  portal: 'Portal des Lieferanten',
  edi: 'EDI',
  phone: 'Telefon',
}

export const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const
/** 1 = Montag … 7 = Sonntag → „Mo, Mi, Fr“ */
export const formatWeekdays = (days: readonly number[]): string =>
  [...days]
    .sort((a, b) => a - b)
    .map((day) => WEEKDAY_SHORT[day - 1] ?? String(day))
    .join(', ')

export const LOCATION_KINDS = ['warehouse', 'store', 'vehicle', 'transit', 'consignment', 'rental_out'] as const
export type LocationKind = (typeof LOCATION_KINDS)[number]
export const LOCATION_KIND_LABELS: Record<LocationKind, string> = {
  warehouse: 'Lager',
  store: 'Filiale',
  vehicle: 'Fahrzeug',
  transit: 'Unterwegs (Transit)',
  consignment: 'Kommission beim Kunden',
  rental_out: 'Verliehen',
}
/** Standorte, an denen Menschen arbeiten und Ware liegt – nur sie dürfen an der Kasse ins Minus. */
export const PHYSICAL_LOCATION_KINDS: readonly LocationKind[] = ['warehouse', 'store', 'vehicle']

export const BIN_KINDS = ['pick', 'bulk', 'receiving', 'shipping', 'returns', 'blocked', 'empties'] as const
export type BinKind = (typeof BIN_KINDS)[number]
export const BIN_KIND_LABELS: Record<BinKind, string> = {
  pick: 'Greifplatz',
  bulk: 'Reserveplatz',
  receiving: 'Wareneingang',
  shipping: 'Warenausgang',
  returns: 'Retouren',
  blocked: 'Sperrplatz',
  empties: 'Leergut',
}

export const UNIT_KINDS = ['piece', 'package', 'mass', 'volume', 'length', 'area', 'time'] as const
export type UnitKind = (typeof UNIT_KINDS)[number]
export const UNIT_KIND_LABELS: Record<UnitKind, string> = {
  piece: 'Stück',
  package: 'Gebinde',
  mass: 'Gewicht',
  volume: 'Volumen',
  length: 'Länge',
  area: 'Fläche',
  time: 'Zeit',
}
/** Maßeinheiten, die als Füllmenge für den Grundpreis taugen (PAngV: je Liter, Kilogramm, Meter, Quadratmeter). */
export const MEASURE_UNIT_KINDS: readonly UnitKind[] = ['mass', 'volume', 'length', 'area']

export const REASON_KINDS = ['adjustment', 'return', 'discount', 'void', 'block', 'cash'] as const
export type ReasonKind = (typeof REASON_KINDS)[number]
export const REASON_KIND_LABELS: Record<ReasonKind, string> = {
  adjustment: 'Bestandskorrektur',
  return: 'Rückgabe',
  discount: 'Rabatt',
  void: 'Storno',
  block: 'Sperre',
  cash: 'Kasse',
}

/** Umsatzsteuer-Kategorien nach EN 16931 (BT-151). */
export const TAX_CATEGORIES = ['S', 'Z', 'E', 'AE', 'K', 'G', 'O'] as const
export type TaxCategory = (typeof TAX_CATEGORIES)[number]
export const TAX_CATEGORY_LABELS: Record<TaxCategory, string> = {
  S: 'S – Regelbesteuerung',
  Z: 'Z – Nullsatz',
  E: 'E – steuerbefreit',
  AE: 'AE – Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG)',
  K: 'K – innergemeinschaftliche Lieferung',
  G: 'G – Ausfuhr',
  O: 'O – nicht steuerbar',
}

// ---- Feldnamen für Verlauf und Vorschau ---------------------------------------------------------------------
// Schlüssel = Feldname in den Verträgen (so steht er in audit_log.changes), Wert = Beschriftung für Menschen.

export const FIELD_LABELS: Record<string, string> = {
  // Artikel
  sku: 'Artikelnummer',
  name: 'Bezeichnung',
  shortName: 'Kurzbezeichnung',
  description: 'Beschreibung',
  itemGroupId: 'Warengruppe',
  kind: 'Art',
  baseUnitId: 'Basiseinheit',
  taxCodeId: 'Steuerschlüssel',
  stockManaged: 'Bestandsführung',
  lotTracked: 'Chargenpflicht',
  dateTracking: 'Haltbarkeitsdatum',
  serialTracked: 'Seriennummernpflicht',
  minShelfLifeDays: 'Mindestrestlaufzeit Verkauf',
  minShelfLifeReceiptDays: 'Mindestrestlaufzeit Wareneingang',
  expiryWarnDays: 'Warnfrist vor Ablauf',
  contentQty: 'Füllmenge',
  contentUnitId: 'Maßeinheit der Füllmenge',
  basePriceRequired: 'Grundpreis ausweisen',
  minAge: 'Altersgrenze',
  alcoholBp: 'Alkoholgehalt',
  packaging: 'Verpackung',
  packagingMaterial: 'Material',
  brand: 'Marke',
  manufacturer: 'Hersteller',
  countryOfOrigin: 'Herkunftsland',
  isPriceFixed: 'Preisbindung',
  consignmentEligible: 'Kommissionsware',
  ownership: 'Eigentum',
  replacementPriceCents: 'Ersatzpreis',
  customFields: 'Zusatzfelder',
  active: 'Status',
  units: 'Gebinde',
  barcodes: 'Barcodes',
  images: 'Bilder',
  // Disposition
  listed: 'Gelistet',
  minQty: 'Mindestbestand',
  reorderQty: 'Meldebestand',
  targetQty: 'Zielbestand',
  defaultBinId: 'Standardplatz',
  // Lieferant am Artikel
  supplierSku: 'Artikelnummer beim Lieferanten',
  orderUnitId: 'Bestelleinheit',
  priceCents: 'Einkaufspreis',
  minOrderQty: 'Mindestbestellmenge',
  leadTimeDays: 'Lieferzeit',
  isPreferred: 'Hauptlieferant',
  validFrom: 'Gültig ab',
  validTo: 'Gültig bis',
  // Partner
  number: 'Nummer',
  type: 'Art',
  name2: 'Namenszusatz',
  isCustomer: 'Kunde',
  isSupplier: 'Lieferant',
  vatId: 'USt-IdNr.',
  taxNumber: 'Steuernummer',
  email: 'E-Mail',
  phone: 'Telefon',
  priceGroupId: 'Preisgruppe',
  paymentTermId: 'Zahlungsbedingung',
  creditLimitCents: 'Kreditlimit',
  discountBp: 'Kundenrabatt',
  minShelfLifeDaysPartner: 'Mindestrestlaufzeit',
  collectiveInvoicing: 'Rechnungsart',
  datevAccountCustomer: 'Debitorennummer',
  datevAccountSupplier: 'Kreditorennummer',
  gln: 'GLN',
  einvoiceFormat: 'E-Rechnung',
  einvoiceAddress: 'Empfangsweg der E-Rechnung',
  buyerReference: 'Käuferreferenz (Leitweg-ID)',
  einvoiceSyntax: 'Syntax der E-Rechnung',
  paymentMethod: 'Zahlart',
  sepaMandate: 'SEPA-Lastschriftmandat',
  requiresOrderRef: 'Bestellnummer Pflicht',
  supplierRefAtCustomer: 'Unsere Lieferantennummer beim Kunden',
  contractRef: 'Vertragsreferenz',
  rebateNote: 'Hinweis auf vereinbarte Entgeltminderungen',
  taxCase: 'Steuerfall',
  registerNumber: 'Registernummer',
  iban: 'IBAN',
  bic: 'BIC',
  accountHolder: 'Kontoinhaber',
  blocked: 'Sperre',
  blockReason: 'Grund der Sperre',
  notes: 'Bemerkung',
  addresses: 'Anschriften',
  contacts: 'Ansprechpartner',
  minOrderValueCents: 'Mindestbestellwert',
  freightFreeFromCents: 'Frachtfrei ab',
  orderWeekdays: 'Bestelltage',
  deliveryWeekdays: 'Liefertage',
  orderChannel: 'Bestellweg',
  orderEmail: 'Bestell-E-Mail',
  // Standort, Lagerplatz
  code: 'Kürzel',
  homeLocationId: 'Heimatstandort',
  supplyLocationId: 'Liefernder Standort',
  allowNegativeStock: 'Kassenabgang ins Minus',
  isEstablishment: 'Betriebsstätte',
  salesAreaM2: 'Verkaufsfläche',
  street: 'Straße',
  zip: 'PLZ',
  city: 'Ort',
  country: 'Land',
  sort: 'Reihenfolge',
  isDefault: 'Standard',
  isAvailable: 'Verfügbar',
  // Lagerplätze (Migration 1002)
  zone: 'Bereich',
  aisle: 'Gang',
  rack: 'Regal',
  shelf: 'Fach',
  locked: 'Gesperrt',
  lockReason: 'Grund der Sperre',
  // Grunddaten
  plural: 'Mehrzahl',
  short: 'Kurzform',
  decimals: 'Nachkommastellen',
  uneceCode: 'UN/ECE-Code',
  key: 'Schlüssel',
  category: 'Kategorie (EN 16931)',
  exemptionReason: 'Befreiungsgrund',
  exemptionCode: 'Befreiungsgrund (VATEX-Code)',
  taxCaseDefault: 'Ersatzschlüssel für den Steuerfall',
  note: 'Hinweis auf dem Beleg',
  datevAccount: 'DATEV-Konto',
  datevTaxKey: 'DATEV-Steuerschlüssel',
  rateBp: 'Steuersatz',
  parentId: 'Übergeordnete Gruppe',
  defaultTaxCodeId: 'Standard-Steuerschlüssel',
  dueDays: 'Zahlungsziel',
  discountDays: 'Skontofrist',
  termDiscountBp: 'Skonto',
  requiresNote: 'Bemerkung erforderlich',
}

export const fieldLabel = (field: string): string => FIELD_LABELS[field] ?? field
